/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import logger from 'js/app/loggerSingleton';
import { useRouter } from 'js/lib/useRouter';
import { isSuperuser } from 'js/lib/user';

import { RouteNames } from 'pages/teach-course/constants';

import { ActionOverflow } from '@coursera/cds-core';
import { ClockOutlineIcon, EmailIcon, FlagIcon, SettingsIcon, SwapIcon, TrashIcon } from '@coursera/cds-icons';

import { isVivaExamEnabled } from 'bundles/academic-integrity/lib/epics';
import { deleteAttempt } from 'bundles/author-assignment-grading/utils/StaffGradedAPIUtils';
import { sgaGradingAppChoice } from 'bundles/grading-common';
import { deprecateLegacySgaGraderEnabled } from 'bundles/grading-common/utils/epicUtils';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import ItemSettingExceptionsDrawer from 'bundles/grading/components/staff-graded-assignments/item-setting-exceptions/ItemSettingExceptionsDrawer';
import PlagiarismDrawer from 'bundles/grading/components/staff-graded-assignments/plagiarism/PlagiarismDrawer';
import SwitchToLegacyGraderConfirmation from 'bundles/grading/components/staff-graded-assignments/top-bar/SwitchToLegacyGraderConfirmation';
import UserChangeLogDrawer from 'bundles/grading/components/staff-graded-assignments/top-bar/UserChangeLogDrawer';
import { isTeamSubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { getSubmitterEmail } from 'bundles/grading/utils/submissionUtils';

import _t from 'i18n!nls/grading';

const styles = {
  container: () => css`
    margin: 0 var(--cds-spacing-200);
  `,
  positionOverride: () => css`
    /* in order to override material-ui's positioning styles we need to add the !important tag for positioning overrides */
    top: calc(var(--cds-spacing-300) * -1) !important;
  `,
};

// Component for more items dropdown
const MoreActions: React.FC = () => {
  const { submissionData, pageData } = useContext(SGAPageContext);
  const { submitter, isTeamAssignment } = submissionData.data || {};
  const { submission } = submissionData.data || {};

  const [isChangeLogDrawerOpen, setIsChangeLogDrawerOpen] = useState(false);
  const [isItemSettingDrawerOpen, setItemSettingDrawerOpen] = useState(false);
  const [isPlagiarismDrawerOpen, setPlagiarismDrawerOpen] = useState(false);
  const [isSwapExperienceDialogOpen, setIsSwapExperienceDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const router = useRouter();

  const vivaExamEnabled = isVivaExamEnabled(pageData.courseId, pageData.currentItemId);

  const toggleChangeLogDrawer = () => {
    setIsChangeLogDrawerOpen((prevState) => !prevState);
  };
  const toggleItemSettingExceptionsDrawer = () => {
    setItemSettingDrawerOpen((prevState) => !prevState);
  };
  const togglePlagiarismDrawer = () => {
    setPlagiarismDrawerOpen((prevState) => !prevState);
  };

  const handleSwapExperience = () => {
    sgaGradingAppChoice.setGradingAppChoice(pageData.courseId, 'legacyGrader');
    setIsSwapExperienceDialogOpen(false);
    router.push({
      name: RouteNames.USER_SUBMISSION[RouteNames.ASSIGNMENT_GRADING_ITEM],
      params: {
        courseSlug: pageData.currentCourseSlug,
        contextId: pageData.currentContextId,
        itemId: pageData.currentItemId,
        id: submitter?.id || '',
      },
      query: {
        ...(pageData.gradingGroupId && { groupId: pageData.gradingGroupId }),
      },
    });
  };

  const handleDeleteAttempt = () => {
    const { currentItemId, courseId } = pageData;
    // eslint-disable-next-line no-alert
    if (!window.confirm(_t('Are you sure you want to delete this submission?'))) {
      return;
    }

    setIsDeleting(true);

    const userId = submitter && isTeamSubmitter(submitter) ? submitter.userProfiles[0].userId : submitter?.userId || '';

    deleteAttempt(userId, currentItemId, courseId)
      .then(() => window.location.reload())
      .fail(() => {
        logger.error('Failed deleting SGA submission attempt.');
      });
  };

  // get the email addresses of the submitter to be used on a 'mailto' href; can be multiple addresses if it's a team, or empty if anonymous assignment
  const mailToEmailAddresses = submitter && getSubmitterEmail(submitter);

  const handleMailto = () => {
    window.location.href = `mailto:${mailToEmailAddresses}`;
  };

  return (
    <React.Fragment>
      <div css={styles.container}>
        <ActionOverflow label={_t('More Actions')} size="small">
          <ActionOverflow.Item icon={<ClockOutlineIcon size="small" />} onClick={toggleChangeLogDrawer}>
            {_t('Change log')}
          </ActionOverflow.Item>
          <ActionOverflow.Item icon={<SettingsIcon size="small" />} onClick={toggleItemSettingExceptionsDrawer}>
            {_t('Item setting exceptions')}
          </ActionOverflow.Item>
          {!vivaExamEnabled && submission && (
            <ActionOverflow.Item icon={<FlagIcon size="small" />} onClick={togglePlagiarismDrawer}>
              {_t('Plagiarism flag')}
            </ActionOverflow.Item>
          )}
          {mailToEmailAddresses && (
            <ActionOverflow.Item icon={<EmailIcon size="small" />} className="pii-hide" onClick={handleMailto}>
              {isTeamAssignment ? _t('Email team') : _t('Email learner')}
            </ActionOverflow.Item>
          )}
          {deprecateLegacySgaGraderEnabled() && (
            <ActionOverflow.Item icon={<SwapIcon size="small" />} onClick={() => setIsSwapExperienceDialogOpen(true)}>
              {_t('Switch to classic grader')}
            </ActionOverflow.Item>
          )}
          {submission && isSuperuser() && (
            <ActionOverflow.Item icon={<TrashIcon size="small" />} onClick={handleDeleteAttempt} disabled={isDeleting}>
              {_t('Delete attempt (SU-only)')}
            </ActionOverflow.Item>
          )}
        </ActionOverflow>
      </div>
      <UserChangeLogDrawer
        key={`change-log-${submitter?.submitterId}`}
        isOpen={isChangeLogDrawerOpen}
        onClose={toggleChangeLogDrawer}
      />
      <ItemSettingExceptionsDrawer
        key={`item-setting-exception-${submitter?.submitterId}`}
        isOpen={isItemSettingDrawerOpen}
        onClose={toggleItemSettingExceptionsDrawer}
      />
      <SwitchToLegacyGraderConfirmation
        isOpen={isSwapExperienceDialogOpen}
        onConfirm={handleSwapExperience}
        onCancel={() => setIsSwapExperienceDialogOpen(false)}
      />
      {!vivaExamEnabled && submission && (
        <PlagiarismDrawer
          key={`plagiarism-${submitter?.submitterId}`}
          isOpen={isPlagiarismDrawerOpen}
          onClose={() => setPlagiarismDrawerOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default MoreActions;
